import React, { useState, useRef, useEffect } from 'react';

import UpdateDateTime from 'components/features/UpdateDateTime';
import AssetAllocatioinChart from 'components/features/asset-transition/AssetAllocationChart';
import AssetAllocationInfo from 'components/features/asset-transition/AssetAllocationInfo';
import NothingMessageView from 'components/features/NothingMessageView';
import { urlParams } from 'components/features/utils/utils';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { formatDate } from 'utils/formatter';
import { getDate } from 'components/features/utils/converter';
import * as api from 'config/api';
import * as consts from 'config/consts';
import { useLoading } from 'hooks/useLoadign';

export type PeriodType = {
  From: Date;
  To: Date;
};
export type SlideDayType = {
  Day: number;
  BaseDay: number;
};

const defaultParams = { dsi: '0' };

// 前日の値をスライドさせて期間内を補正する
function getAssetSlideItems(assetList: api.GetAssetResultItem[]) {
  const toItem = assetList[0];
  const toYM = Math.floor(toItem.Day / 100);
  const toY = Math.floor(toItem.Day / 10000);
  const toM = toYM - toY * 100 - 1;
  const toD = toItem.Day - toYM * 100;
  const toDate = new Date(toY, toM, toD).getDate();

  const slideItems: SlideDayType[] = [];
  const newItems: api.GetAssetResultItem[] = [];
  for (let n = 0; n < assetList.length; n++) {
    const base = assetList[n];

    // 祝日の補填が終われば（同日付になれば）breakするので、
    // 長い期間を繰り返すことはないが、補填する期間としては、実データ数+10年分とする
    for (let c = newItems.length; c < assetList.length + 3660; c++) {
      const compDay = new Date(toY, toM, toD);
      compDay.setDate(toDate - c);
      const nCompDay = Number(formatDate(compDay, 'yyyyMMdd'));

      if (base.Day === nCompDay) {
        newItems.push({ ...assetList[n] });
        break;
      } else if (assetList[n].Day < nCompDay) {
        slideItems.push({ Day: nCompDay, BaseDay: base.Day });
        newItems.push({ ...base, Day: nCompDay });
      }
    }
  }
  return { assetList: newItems, slideList: slideItems };
}

// 「年・月・週」の指定により、対象期間を算出
export function getTargetPeriod(
  asset: api.GetAssetResult,
  dateSelectIndex: number,
  baseDay: Date,
  shift = 0
): PeriodType {
  // 全データ内の開始日・終了日を取得
  const limitPeriod = getLimitDate(asset);
  // 開始日を数値化
  const nLimitFrom = getNumberYMD(limitPeriod.From);
  // 終了日を数値化
  let nLimitTo = getNumberYMD(limitPeriod.To);
  // 基準日を数値化
  const nBase = getNumberYMD(baseDay);
  // 後で、基準日をシフトさせる為の日付
  const newBaseDay = new Date(baseDay);

  // 種別に合わせて、範囲を限定
  // 「直近」
  if (dateSelectIndex === consts.ChartPeriodTypes.直近) {
    // 基準日を「直近14日」シフト
    const nShiftDays = 14;
    const nShiftDay = shift === 0 ? -1 * (nShiftDays - 1) : shift * nShiftDays;
    newBaseDay.setDate(nBase[2] + nShiftDay);
    let nTarget = [
      newBaseDay.getFullYear(),
      newBaseDay.getMonth(),
      newBaseDay.getDate(),
    ];
    const nTargetYMD = nTarget[0] * 10000 + nTarget[1] * 100 + nTarget[2];

    // 範囲期間（年月日）
    const nLimitFromYMD =
      nLimitFrom[0] * 10000 + nLimitFrom[1] * 100 + nLimitFrom[2];
    const dtTo = new Date(limitPeriod.To);
    dtTo.setDate(nLimitTo[2] - (nShiftDays - 1));
    nLimitTo = getNumberYMD(dtTo);
    const nLimitToYMD = nLimitTo[0] * 10000 + nLimitTo[1] * 100 + nLimitTo[2];

    // 期間チェック（年月日）
    if (shift === 1 && nLimitToYMD < nTargetYMD) {
      nTarget = nLimitTo;
    } else if (shift === -1 && nLimitFromYMD > nTargetYMD) {
      nTarget = nLimitFrom;
    }

    // 対象期間
    return {
      From: new Date(nTarget[0], nTarget[1], nTarget[2]),
      To: new Date(nTarget[0], nTarget[1], nTarget[2] + (nShiftDays - 1)),
    };

    // 「週」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.週) {
    // 基準日を「1週間」シフト（From:日曜日 To:土曜日）
    const nShiftDays = 7;
    newBaseDay.setDate(nBase[2] - baseDay.getDay() + shift * nShiftDays);
    const nTarget = [
      newBaseDay.getFullYear(),
      newBaseDay.getMonth(),
      newBaseDay.getDate(),
    ];

    /* 先に日曜日から土曜日の1週間を算出し、レコードを生成しているので、期間チェック不要 */
    // 期間チェック（年月日）
    // let nTargetYMD = nTarget[0] * 10000 + nTarget[1] * 100 + nTarget[2];
    // let nLimitFromYMD = nLimitFrom[0] * 10000 + nLimitFrom[1] * 100 + nLimitFrom[2];
    // let nLimitToYMD = nLimitTo[0] * 10000 + nLimitTo[1] * 100 + nLimitTo[2];
    // if (shift === 1 && nLimitToYMD < nTargetYMD) {
    //   nTarget = nLimitTo;
    // } else if (shift === -1 && nLimitFromYMD > nTargetYMD) {
    //   nTarget = nLimitFrom;
    // }

    // 対象期間
    return {
      From: new Date(nTarget[0], nTarget[1], nTarget[2]),
      To: new Date(nTarget[0], nTarget[1], nTarget[2] + (nShiftDays - 1)),
    };

    // 「月」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.月) {
    // 基準日を「1か月」シフト
    newBaseDay.setMonth(nBase[1] + shift);
    const nTarget = [newBaseDay.getFullYear(), newBaseDay.getMonth()];
    const nTargetYM = nTarget[0] * 100 + nTarget[1];

    // 範囲期間（年月）
    const nLimitFromYM = nLimitFrom[0] * 100 + nLimitFrom[1];
    const nLimitToYM = nLimitTo[0] * 100 + nLimitTo[1];

    // 期間チェック（年月）
    if (shift === 1 && nLimitToYM < nTargetYM) {
      nTarget[0] = nLimitTo[0];
      nTarget[1] = nLimitTo[1];
    } else if (shift === -1 && nLimitFromYM > nTargetYM) {
      nTarget[0] = nLimitFrom[0];
      nTarget[1] = nLimitFrom[1];
    }

    // 対象期間
    return {
      From: new Date(nTarget[0], nTarget[1], 1),
      To: new Date(nTarget[0], nTarget[1] + 1, 0),
    };

    // 「年」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.年) {
    // 基準日を「1年」シフト
    newBaseDay.setFullYear(nBase[0] + shift);
    const nTarget = [newBaseDay.getFullYear()];
    const nTargetY = nTarget[0];

    // 範囲期間（年）
    const nLimitFromY = nLimitFrom[0];
    const nLimitToY = nLimitTo[0];

    // 期間チェック（年）
    if (shift === 1 && nLimitToY < nTargetY) {
      nTarget[0] = nLimitTo[0];
    } else if (shift === -1 && nLimitFromY > nTargetY) {
      nTarget[0] = nLimitFrom[0];
    }

    // 対象期間
    return {
      From: new Date(nTarget[0], 0, 1),
      To: new Date(nTarget[0], 11, 31),
    };
  }
  return { From: baseDay, To: baseDay };
}

// 日付型を年月日の数値に変換
function getNumberYMD(date: Date): number[] {
  return [date.getFullYear(), date.getMonth(), date.getDate()];
}

// 実データの開始日・終了日を取得
function getLimitDate(asset: api.GetAssetResult): PeriodType {
  const st = asset.Items.length - 1;
  const ed = 0;
  return {
    From: getDate(asset.Items[st].Day),
    To: getDate(asset.Items[ed].Day),
  };
}

// 資産変動元のAsset情報を抽出
export function getPrevAssetItem(
  assetList: api.GetAssetResultItem[],
  period: PeriodType
): api.GetAssetResultItem | undefined {
  const nFrom = Number(formatDate(period.From, 'yyyyMMdd'));
  let preAssetItem: api.GetAssetResultItem | undefined = undefined;
  // assetListは日付降順
  for (let n = 0; n < assetList.length; n++) {
    if (assetList[n].Day < nFrom) {
      preAssetItem = assetList[n];
      break;
    }
  }
  // 末データがないときは、初データを採用
  if (preAssetItem === undefined && assetList.length > 0) {
    preAssetItem = assetList[assetList.length - 1];
  }
  return preAssetItem;
}

// Asset情報から対象期間を抽出
export function judgeAssetData(
  assetList: api.GetAssetResultItem[],
  period: PeriodType,
  dateSelectIndex: number
): api.GetAssetResultItem[] {
  const assetItems: api.GetAssetResultItem[] = [];

  // 期間フィルタできない場合は除外
  if (period.From === undefined || period.To === undefined) {
    return assetList;
  }

  // 期間フィルタ
  const nFrom = Number(formatDate(period.From, 'yyyyMMdd'));
  const nTo = Number(formatDate(period.To, 'yyyyMMdd'));
  let periodList = assetList.filter(value => {
    return value.Day >= nFrom && value.Day <= nTo;
  });

  // 種別に合わせて、仮想データを付与
  // 「直近」
  if (dateSelectIndex === consts.ChartPeriodTypes.直近) {
    const dtTo = new Date(period.To);
    const dtFrom = new Date(period.From);
    for (let dt = dtTo; dt >= dtFrom; dt.setDate(dt.getDate() - 1)) {
      const nYMD = Number(formatDate(dt, 'yyyyMMdd'));
      const item = periodList.find(value => value.Day === nYMD);
      assetItems.push(item ? item : newAssetItem(nYMD));
    }

    // 「週」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.週) {
    const dtTo = new Date(period.To);
    const nTo = getNumberYMD(dtTo);
    // To:土曜日
    dtTo.setDate(nTo[2] - dtTo.getDay() + 6);

    const dtFrom = new Date(period.From);
    const nFrom = getNumberYMD(dtFrom);
    // From:日曜日
    dtFrom.setDate(nFrom[2] - dtFrom.getDay());

    // 日曜日から土曜日になるように、レコードを調整
    for (let dt = dtTo; dt >= dtFrom; dt.setDate(dt.getDate() - 1)) {
      const nYMD = Number(formatDate(dt, 'yyyyMMdd'));
      const item = periodList.find(value => value.Day === nYMD);
      assetItems.push(item ? item : newAssetItem(nYMD));
    }

    // 「月」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.月) {
    const dtTo = new Date(period.To);
    const dtFrom = new Date(period.From);
    for (let dt = dtTo; dt >= dtFrom; dt.setDate(dt.getDate() - 1)) {
      const nYMD = Number(formatDate(dt, 'yyyyMMdd'));
      const item = periodList.find(value => value.Day === nYMD);
      assetItems.push(item ? item : newAssetItem(nYMD));
    }

    // 「年」
  } else if (dateSelectIndex === consts.ChartPeriodTypes.年) {
    // 「年」の場合は、月末のデータのみ利用するので、月替わりの判定
    let preYearMonth = '';
    periodList = periodList.filter(o => {
      const nowYearMonth = Math.floor(o.Day / 100).toString();
      if (preYearMonth === '' || preYearMonth !== nowYearMonth) {
        preYearMonth = nowYearMonth;
        return true;
      }
      return false;
    });

    const nYear = Math.floor(periodList[0].Day / 10000);
    for (let nMonth = 12; nMonth >= 1; nMonth--) {
      const nYM = nYear * 100 + nMonth;
      const item = periodList.find(value => {
        return Math.floor(value.Day / 100) === nYM;
      });
      const nYMD = Number(formatDate(new Date(nYear, nMonth, 0), 'yyyyMMdd'));
      assetItems.push(item ? item : newAssetItem(nYMD));
    }
  }

  return assetItems;
}

// 日付のみの仮想データ
function newAssetItem(ymd: number): api.GetAssetResultItem {
  return {
    Day: ymd,
    TotalAssetAmount: null,
    DiffTotalAssetAmount: null,
    DiffTotalAssetRate: null,
    MarginProfitOrLoss: null,
    TotalProfitOrLoss: null,
    DiffTotalProfitOrLoss: null,
    DiffTotalProfitOrLossRate: null,
    StockAmount: null,
    MarginAmount: null,
    RoboAdvisorAmount: null,
    USStockAmount: null,
    InvestmentTrustAmount: null,
    CashAmount: null,
    OtherAmount: null,
    UnlistedStockAmount: null,
    EtfEtnAmount: null,
    ForeignEtfEtnAmount: null,
    ReitAmount: null,
    ForeignReitAmount: null,
    BondAmount: null,
    ForeignBondAmount: null,
    EbAmount: null,
    ForeignEbAmount: null,
    RealEstateAmount: null,
    Incorrect: false,
  };
}

// 損益算出
export function getChangeTotalAssetAmount(
  assetList: api.GetAssetResultItem[],
  prevAssetItem: api.GetAssetResultItem | undefined
): number | undefined {
  let change = undefined;

  if (
    assetList.length <= 0 ||
    prevAssetItem === undefined ||
    prevAssetItem.TotalAssetAmount === null
  ) {
    return change;
  }

  const lst = assetList.filter(x => x.TotalAssetAmount !== null);
  if (lst.length > 0 && lst[0].TotalAssetAmount !== null) {
    change = lst[0].TotalAssetAmount - prevAssetItem.TotalAssetAmount;
  }

  return change;
}

// ダミーデータ生成
// function getDummyAssetItems(): api.GetAssetResultItem[]{
//   const dummy: api.GetAssetResultItem[] = [];
//   let dt = new Date();
//   // let dt = new Date(2021, 0, 23);
//   for(let n=0; n<632; n++) {
//     let newDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - n);
//     let chk = newDay.getDay();
//     if (chk === 0 || chk === 6) continue;

//     const min = 200000;
//     const max = 3000000;
//     const stockAmount = (Math.floor(Math.random() * (max - min)) + min);
//     const marginAmount = (Math.floor(Math.random() * (max - min)) + min);
//     const cashAmount = (Math.floor(Math.random() * (max - min)) + min);
//     const otherAmount = (Math.floor(Math.random() * (max - min)) + min);

//     dummy.push(
//       {
//         Day: Number(formatDate(newDay, 'yyyyMMdd')),
//         TotalAssetAmount: stockAmount + marginAmount + cashAmount + otherAmount,
//         DiffTotalAssetAmount: 0,
//         DiffTotalAssetRate: 0,
//         TotalProfitOrLoss: 0,
//         DiffTotalProfitOrLoss: 0,
//         DiffTotalProfitOrLossRate: 0,
//         StockAmount: stockAmount,
//         MarginAmount: marginAmount,
//         CashAmount: cashAmount,
//         OtherAmount: otherAmount,
//       }
//     )
//   }
//   return dummy;
// }

const AssetTransition: React.FC = () => {
  const { setIsShowMenu } = useGlobalMenuContext();

  const [asset, setAsset] = useState<api.GetAssetResult | undefined>(undefined);
  const [brokers, setBrokers] = useState<
    api.GetUserBrokerResultItem[] | undefined
  >(undefined);
  const [targetPeriod, setTargetPeriod] = useState<PeriodType | undefined>(
    undefined
  );
  const [assetList, setAssetList] = useState<
    api.GetAssetResultItem[] | undefined
  >(undefined);
  const [prevAsset, setPrevAsset] = useState<
    api.GetAssetResultItem | undefined
  >(undefined);
  const [dateSelectIndex, setDateSelectIndex] = useState<number>(() =>
    Number(urlParams(defaultParams).dsi)
  );
  const [changeTotalAssetAmount, setChangeTotalAssetAmount] = useState<
    number | undefined
  >(undefined);
  const [slideList, setSlideList] = useState<SlideDayType[] | undefined>(
    undefined
  );
  const unmounted = useRef<boolean>(false);

  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ローディング対応
  const [, { disableLoading }] = useLoading(true);
  useEffect(() => {
    if (asset && brokers) {
      disableLoading();
    }
  }, [asset, brokers, disableLoading]);

  function chagePeriod(selectIndex: number, shift: number): void {
    if (
      targetPeriod !== undefined &&
      asset?.Items !== undefined &&
      asset?.Items.length > 0
    ) {
      const baseDay =
        shift === 0 ? getDate(asset?.Items[0].Day) : targetPeriod.From;

      setTargetPeriod(getTargetPeriod(asset, selectIndex, baseDay, shift));
      if (dateSelectIndex !== selectIndex) {
        setDateSelectIndex(selectIndex);
      }
    }
  }

  useEffect(() => {
    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    api.getAsset().then(ret => {
      if (!unmounted.current && ret) {
        if (ret.Items && ret.Items.length > 0) {
          // ret.Items = getDummyAssetItems();
          // データがない日は、前日の値をスライドさせて期間内を補正する
          const slide = getAssetSlideItems(ret.Items);
          ret.Items = slide.assetList;
          setSlideList(slide.slideList);
          //--
          const baseDay = getDate(ret.Items[0].Day);
          // 対象期間の算出
          setTargetPeriod(getTargetPeriod(ret, dateSelectIndex, baseDay));
        }
        // Asset情報
        setAsset(ret);
      }
    });
    api.getUserBroker().then(ret => {
      // Brokers情報
      !unmounted.current && ret && setBrokers(ret.Items);
    });
  }, [setAsset, dateSelectIndex]);

  useEffect(() => {
    if (asset === undefined || targetPeriod === undefined) return;

    // 対象期間のAsset情報の抽出
    const judgeAssetList = judgeAssetData(
      asset?.Items,
      targetPeriod,
      dateSelectIndex
    );
    setAssetList(judgeAssetList);

    // 資産変動元のAsset情報を抽出
    const prevAssetItem = getPrevAssetItem(asset?.Items, targetPeriod);
    setPrevAsset(prevAssetItem);

    // 損益算出
    const changeValue = getChangeTotalAssetAmount(
      judgeAssetList,
      prevAssetItem
    );
    setChangeTotalAssetAmount(changeValue);
  }, [asset, setAssetList, targetPeriod, dateSelectIndex]);

  const isAssetAllocatioin =
    brokers !== undefined &&
    brokers.length > 0 &&
    asset !== undefined &&
    asset.Items.length > 0 &&
    assetList !== undefined;

  const today =
    asset?.Items && asset?.Items.length > 0
      ? asset?.Items[0].Day
      : Number(formatDate(new Date(), 'yyyyMMdd'));

  const updateBrokers = (brokers: api.GetUserBrokerResultItem[]) => {
    setBrokers(brokers);
  };

  return (
    <div className="ui-gradient-mt">
      <div className="flex">
        <UpdateDateTime brokers={brokers} updateBrokers={updateBrokers} />
      </div>

      <NothingMessageView brokers={brokers} asset={asset} />

      {isAssetAllocatioin && (
        <div className="ui-card m-2">
          <AssetAllocatioinChart
            assetList={assetList}
            changeTotalAssetAmount={changeTotalAssetAmount}
            dateSelectIndex={dateSelectIndex}
            limitPeriod={asset ? getLimitDate(asset) : undefined}
            targetPeriod={targetPeriod}
            chagePeriod={chagePeriod}
            today={today}
          />
          <AssetAllocationInfo
            assetList={assetList}
            prevAsset={prevAsset}
            dateSelectIndex={dateSelectIndex}
            today={today}
            slideList={slideList}
          />
        </div>
      )}
    </div>
  );
};

export default AssetTransition;
